import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * IdleLogoutManager
 *
 * Automatically signs the user out after IDLE_TIMEOUT_MS of inactivity.
 * Applies to every authenticated route (admin, supervisor, employee).
 *
 * Activity signals: mousemove, mousedown, keydown, touchstart, scroll,
 * wheel, click, and successful outbound API calls (via a global axios
 * response interceptor). Any of these resets the timer.
 *
 * UX: at IDLE_TIMEOUT_MS - WARNING_LEAD_MS the user sees a countdown
 * modal ("You will be signed out in Xs — press to stay"). While the
 * modal is visible, activity does NOT auto-dismiss it — the user must
 * explicitly click "Stay signed in" so we don't lose the warning to a
 * stray mousemove from a curious cat.
 *
 * On timeout: token cleared, redirected to /login?reason=inactivity,
 * toast displays the required copy.
 */

const IDLE_TIMEOUT_MS = parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MS || '', 10) || (5 * 60 * 1000);   // 5 minutes (per product spec)
const WARNING_LEAD_MS = parseInt(process.env.REACT_APP_IDLE_WARNING_LEAD_MS || '', 10) || (30 * 1000);       // 30-second warning countdown
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
  'click',
];

export default function IdleLogoutManager() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pages that should NEVER auto-logout the user. On /verify the user is
  // usually reading an email on another device to grab the 6-digit code —
  // idle-logging them out mid-verification would be maddening. Add other
  // paths here if a new "please-wait" flow appears in the future.
  // "Remember me" sessions opt out of the idle auto-logout entirely.
  const rememberedSession = localStorage.getItem('remember_me') === '1';
  const idleImmuneRoute = location.pathname === '/verify' || rememberedSession;

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  // Ref shadow of `showWarning` so event listeners always see the current
  // value without needing to be re-registered on every state change.
  const showWarningRef = useRef(false);

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(WARNING_LEAD_MS / 1000));

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    idleTimerRef.current = null;
    warningTimerRef.current = null;
    countdownIntervalRef.current = null;
  }, []);

  const doLogout = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    logout();
    toast.error('You have been logged out due to inactivity. Please log in again.', {
      duration: 8000,
    });
    navigate('/login?reason=inactivity', { replace: true });
  }, [clearAllTimers, logout, navigate]);

  const startCountdown = useCallback(() => {
    showWarningRef.current = true;
    setSecondsLeft(Math.round(WARNING_LEAD_MS / 1000));
    setShowWarning(true);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          // Cannot call doLogout here directly (closure) — clear the
          // interval and let the effect below trigger the logout.
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const armTimers = useCallback(() => {
    clearAllTimers();
    warningTimerRef.current = setTimeout(startCountdown, IDLE_TIMEOUT_MS - WARNING_LEAD_MS);
    idleTimerRef.current = setTimeout(doLogout, IDLE_TIMEOUT_MS);
  }, [clearAllTimers, startCountdown, doLogout]);

  const onActivity = useCallback(() => {
    // While the warning modal is up, activity does NOT silently dismiss
    // it — the user must click "Stay signed in" explicitly.
    if (showWarningRef.current) return;
    armTimers();
  }, [armTimers]);

  const stayActive = useCallback(() => {
    showWarningRef.current = false;
    setShowWarning(false);
    armTimers();
  }, [armTimers]);

  // Mount: attach listeners + start timer. Depends only on `user` so we
  // don't re-register listeners on every render.
  useEffect(() => {
    if (!user || idleImmuneRoute) {
      clearAllTimers();
      showWarningRef.current = false;
      setShowWarning(false);
      return undefined;
    }
    armTimers();
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, onActivity, { passive: true });
    });
    const interceptorId = axios.interceptors.response.use(
      (response) => {
        onActivity();
        return response;
      },
      (error) => Promise.reject(error),
    );
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, onActivity);
      });
      axios.interceptors.response.eject(interceptorId);
      clearAllTimers();
    };
  }, [user, onActivity, armTimers, clearAllTimers, idleImmuneRoute]);

  // When the countdown hits 0, force logout. Kept in a separate effect so
  // the visible timer and the actual logout stay in sync.
  useEffect(() => {
    if (showWarning && secondsLeft <= 0) {
      doLogout();
    }
  }, [showWarning, secondsLeft, doLogout]);

  if (!user) return null;

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent data-testid="idle-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you still there?</AlertDialogTitle>
          <AlertDialogDescription>
            You will be signed out in{' '}
            <span
              className="font-semibold text-foreground"
              data-testid="idle-warning-countdown"
            >
              {secondsLeft}
            </span>{' '}
            second{secondsLeft === 1 ? '' : 's'} due to inactivity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={stayActive}
            data-testid="idle-stay-signed-in-btn"
          >
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
